import React, { useState, useEffect, useContext } from 'react';
import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, query, getDocs, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { AuthContext } from '../AuthContext';
import { FaTrash } from 'react-icons/fa';
import './Gallery.css';

const Gallery = () => {
    const { user, isAdmin } = useContext(AuthContext);
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [deletingImage, setDeletingImage] = useState(null);

    const collectionName = 'gallery';

    const getMediaType = (file) =>
        file.type.startsWith('video/') ? 'video' : 'image';

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setUploading(true);
            setError(null);

            const path = `gallery/${user.uid}/${Date.now()}_${file.name}`;
            const storageRef = ref(storage, path);

            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            await addDoc(collection(db, collectionName), {
                url: downloadURL,
                filename: file.name,
                timestamp: new Date().toISOString(),
                userId: user.uid,
                path: path,
                mediaType: getMediaType(file)
            });
        } catch (err) {
            console.error('Error uploading image:', err);
            setError(`Failed to upload: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };

    // Add delete functionality
    const handleDeleteImage = async (imageId, imagePath, e) => {
        e.stopPropagation(); // Prevent any parent click events
        
        if (!isAdmin || !imageId) return;
        
        try {
            setDeletingImage(imageId);
            setError(null);
            
            // Delete from Firestore
            await deleteDoc(doc(db, collectionName, imageId));
            
            // Delete from Storage if path exists
            if (imagePath) {
                const storageRef = ref(storage, imagePath);
                await deleteObject(storageRef);
            }
            
            // Update UI optimistically (though onSnapshot will handle this)
            setImages(prevImages => prevImages.filter(img => img.id !== imageId));
            
        } catch (error) {
            console.error("Error deleting image:", error);
            setError(`Failed to delete image: ${error.message}`);
        } finally {
            setDeletingImage(null);
        }
    };

    useEffect(() => {
        const q = query(collection(db, collectionName), orderBy('timestamp', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const imageList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setImages(imageList);
        });

        return () => unsubscribe();
    }, []);

    return (
        <div className="gallery-container">
            <h2>Gallery</h2>
            
            {/* Add the teaser description section */}
            <div className="gallery-teaser">
                <h3>Teasers!</h3>
                <p>Subscribe to see full images and more</p>
            </div>
            
            {isAdmin && (
                <div className="upload-section">
                    <input
                        type="file"
                        accept="image/*,video/mp4,video/webm,video/quicktime"
                        onChange={handleImageUpload}
                        disabled={uploading}
                        id="image-upload"
                        className="hidden-input"
                    />
                    <label htmlFor="image-upload" className="upload-button">
                        {uploading ? 'Uploading...' : 'Upload Image, GIF or Short Video'}
                    </label>
                    {error && <p className="error-message">{error}</p>}
                </div>
            )}

            <div className="gallery-grid">
                {images.map((image) => (
                    <div key={image.id} className="gallery-item">
                        {image.mediaType === 'video' ? (
                            <video
                                src={image.url}
                                loop
                                muted
                                autoPlay
                                playsInline
                                className="gallery-media"
                                aria-label={image.filename}
                            />
                        ) : (
                            <img
                                src={image.url}
                                alt={image.filename}
                                loading="lazy"
                            />
                        )}
                        
                        {/* Add delete button for admins */}
                        {isAdmin && (
                            <button 
                                className="delete-button"
                                onClick={(e) => handleDeleteImage(image.id, image.path, e)}
                                disabled={deletingImage === image.id}
                                title="Delete this image"
                            >
                                <FaTrash />
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Gallery;
